import { Option, Value } from 'baseui/select';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  CruiseSaleCruiseSaleAddOnsFormType,
  CruiseSaleFormType,
} from '../form-type';
import { addNewAddOnsItem } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function useGetCruiseAddOnsTableList(parentIndex: number = 0) {
  const { editable } = useFormState();
  const { t } = useTranslation();
  const { control } = useFormContext<CruiseSaleFormType>();
  const cruiseSaleGuestName =
    `cruiseSales.${parentIndex}.cruiseSaleGuests` as const;

  const guests = useWatch({
    control,
    name: cruiseSaleGuestName,
  });

  const guestList = React.useMemo<Option[]>(() => {
    return guests
      .filter((guest) => !!guest.guestId)
      .map((guest) => {
        return {
          id: guest.guestId,
          label: guest.guestName,
          extra: guest,
        };
      });
  }, [guests]);

  const columns = React.useMemo<
    IColumn<CruiseSaleCruiseSaleAddOnsFormType>[]
  >(() => {
    return [
      {
        header: t('common:guest'),
        accessorKey: 'guestId',
        minSize: 250,
        dataType: ColumnDataType.select,
        options: guestList,
        required: true,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 500,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, guestList, t]);

  return columns;
}

export default function CruiseAddOnsTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const name = `cruiseSales.${props.parentIndex}.cruiseSaleAddOns` as const;

  const { trigger, getValues, control } = useFormContext<CruiseSaleFormType>();

  const { fields, update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetCruiseAddOnsTableList(props.parentIndex);

  const dispatch = React.useCallback(
    (action) => {
      const addons = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const addon = addons[currentRowIndex];
      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof CruiseSaleCruiseSaleAddOnsFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<CruiseSaleCruiseSaleAddOnsFormType> =
        () => {
          if (columnId === 'guestId') {
            return { guestName: extra?.[0]?.label as string };
          }
          return {};
        };

      if (type === 'addRow') {
        append(addNewAddOnsItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...addon,
          [columnId]: value,
          ...otherVal(),
        });
        trigger(name);
      }
    },
    [append, getValues, name, remove, trigger, update],
  );

  if (fields.length || editable) {
    return (
      <>
        <Text variant="ParagraphSmall">{t('ticketSales:add_ons')}</Text>
        <Separator gap={12} />
        <div style={{ width: '100%' }}>
          <TableInput
            columns={columns}
            data={fields}
            dispatch={dispatch}
            disabled={!editable}
            name={name}
            uniqueRowKey="customId"
          />
        </div>
      </>
    );
  }

  return null;
}
